import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Twitter, Linkedin, Facebook } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api } from "../api";

export default function InsightDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/articles/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 text-center" data-testid="article-not-found">
        <h1 className="font-display text-3xl font-bold text-slate-100">Article not found</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 text-blue-400 hover:text-blue-300">
          <ArrowLeft size={15} /> Back to Insights
        </Link>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-40 pb-32 flex justify-center" data-testid="article-loading">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  const { article, related } = data;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <main className="pt-32 pb-24" data-testid="article-page">
      <Seo title={article.seo_title || article.title} description={article.seo_description || article.summary} />
      <div className="max-w-3xl mx-auto px-4 sm:px-8">
        <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-blue-300 transition-colors">
          <ArrowLeft size={15} /> All insights
        </Link>
        <div className="mt-8 flex flex-wrap items-center gap-3 text-[11px] font-mono-accent uppercase tracking-widest text-slate-500">
          <span className="text-blue-300 border border-blue-500/30 rounded-full px-3 py-1">{article.category}</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {article.reading_time} min read</span>
          <span>{article.author}</span>
        </div>
        <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-50 leading-[1.08]" data-testid="article-title">
          {article.title}
        </h1>
        <div className="mt-10 space-y-6" data-testid="article-content">
          {article.content.split(/\n\n+/).map((block, i) =>
            block.startsWith("## ") ? (
              <h2 key={i} className="font-display text-xl sm:text-2xl font-bold text-slate-50 pt-4">{block.replace(/^##\s*/, "")}</h2>
            ) : (
              <p key={i} className="text-base sm:text-lg text-slate-400 leading-relaxed">{block}</p>
            )
          )}
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex items-center gap-4">
          <span className="text-sm text-slate-500">Share:</span>
          <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" data-testid="share-x" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500/60 transition-all">
            <Twitter size={15} />
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" data-testid="share-linkedin" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500/60 transition-all">
            <Linkedin size={15} />
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" data-testid="share-facebook" className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:text-blue-400 hover:border-blue-500/60 transition-all">
            <Facebook size={15} />
          </a>
        </div>

        {related.length > 0 && (
          <Reveal className="mt-16">
            <h2 className="font-display text-2xl font-bold text-slate-50 mb-6">Related Articles</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/insights/${r.slug}`}
                  data-testid={`related-article-${r.slug}`}
                  className="group rounded-2xl border border-slate-800 bg-slate-900/60 p-5 hover:border-blue-500/50 transition-all duration-300"
                >
                  <p className="font-mono-accent text-[9px] uppercase tracking-widest text-blue-300/80">{r.category}</p>
                  <h3 className="mt-2 font-display text-[15px] font-bold text-slate-100 leading-snug group-hover:text-blue-300 transition-colors">{r.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-400">
                    Read <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
}
